import { fetchPublicPosts } from "./publicBlogApi";
import { displayLabel } from "../../utils/displayLabel";
// components/public/ReaderShopLayout.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, Users, Newspaper, PenSquare, FileText as PageIcon, ShoppingCart } from "lucide-react";
import { useAuthStatus } from "../../hooks/useAuthStatus";
import { getCurrentReader } from "../../auth/getCurrentReader";
import { resolveSessionAcrossPools } from "../../auth/resolveSession";
import Logo from "../../assets/TheBC.png";
import { fetchCustomPagesForHost } from "./CustomPagesApi";
import {
  followGroup,
  unfollowGroup,
  followAuthor,
  getReaderFollowGraph,
  unfollowAuthor,
} from "../../services/followService";
import { getAuthorCartCount } from "./AuthorCart";
import usePublicAuthor from '../../hooks/usePublicAuthor';

const ROOT_HOST = "bookishcircle.com";
const isOnAuthorSubdomain = () => {
  const host = window.location.hostname;
  return (
    host.endsWith(`.${ROOT_HOST}`) &&
    host !== ROOT_HOST &&
    host !== `www.${ROOT_HOST}`
  );
};

const canonicalUrl = (path = "/") =>
  `https://${ROOT_HOST}${path.startsWith("/") ? path : `/${path}`}`;

export const getReaderLoginPath = (returnTo) =>
  `/loginreader?returnTo=${encodeURIComponent(returnTo)}`;

const WELCOME_API =
  "https://v70pmk2q7a.execute-api.us-west-2.amazonaws.com/default/get-welcome";

const ReaderShopLayout = ({ group, children }) => {
  const { user } = useAuthStatus({ disableOnSubdomain: true });
  const location = useLocation();
  const navigate = useNavigate();

  const [showDashboardButton, setShowDashboardButton] = useState(false);
  const [hasBlog, setHasBlog] = useState(false);
  const [customPages, setCustomPages] = useState([]);
  const [shopOwnerSub, setShopOwnerSub] = useState("");

  const [isFollowingAuthor, setIsFollowingAuthor] = useState(false);
  const [isAuthorFollowLoading, setIsAuthorFollowLoading] = useState(false);
  const [authorFollowError, setAuthorFollowError] = useState("");
  const [readerSession, setReaderSession] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const circleRequest = useRef(0);

  const [brand, setBrand] = useState({
    profileImageUrl: "",
    penName: "",
    theme: {
      primary: "",
      accent: "",
      bgImage: "",
      bannerImage: "",
    },
  });

  const host = useMemo(
    () => (typeof window !== "undefined" ? window.location.host : ""),
    []
  );

  const authorHost = isOnAuthorSubdomain();
  const publicAuthor = usePublicAuthor(group ? null : host);

  useEffect(() => {
    if (!authorHost) return;
    const p = location.pathname || "/";
    if (p.startsWith("/market") || p.startsWith("/authorportal")) {
      window.location.replace(canonicalUrl(p));
    }
  }, [authorHost, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    if (group) { setShopOwnerSub(group.subdomain ? `${group.subdomain}.bookishcircle.com` : ""); setBrand({ profileImageUrl: group.welcome?.profileImageUrl || "", penName: group.groupName, theme: {} }); return; }
    (async () => {
      try {
        const res = await fetch(
          `${WELCOME_API}?host=${encodeURIComponent(host)}`
        );
        if (!res.ok) throw new Error(`Welcome API ${res.status}`);
        const data = await res.json();

        const theme = {
          primary: data?.theme?.primary || "",
          accent: data?.theme?.accent || "",
          bgImage: data?.theme?.bgImage || "",
          bannerImage: data?.theme?.bannerImage || "",
        };

        if (!cancelled) {
          setBrand({
            profileImageUrl: data?.profileImageUrl || "",
            penName: data?.penName || "",
            theme,
          });
        }

        if (!cancelled) {
          try {
            const session = await resolveSessionAcrossPools({ persist: false });
            const loggedInSub = session?.user?.userId;
            const shopOwnerSub = data?.sub;

            setShowDashboardButton(
              loggedInSub && shopOwnerSub && loggedInSub === shopOwnerSub
            );
            setShopOwnerSub(shopOwnerSub || "");
          } catch {
            setShowDashboardButton(false);
          }
        }
      } catch {
        if (!cancelled) setShowDashboardButton(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [host, user, group]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { pages } = group ? { pages: group.pages || [] } : await fetchCustomPagesForHost(host);
        if (!cancelled) {
          setCustomPages(pages);
        }
      } catch {
        if (!cancelled) setCustomPages([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [host, group]);

  useEffect(() => {
    if (group) { setHasBlog(!!group.posts?.length); return; }
    const controller = new AbortController();
    fetchPublicPosts(host, controller.signal).then(posts => setHasBlog(posts.length > 0)).catch(() => { if (!controller.signal.aborted) setHasBlog(false); });
    return () => controller.abort();
  }, [host, group]);

  useEffect(() => {
    const r = document.documentElement;
    if (brand.theme.primary)
      r.style.setProperty("--brand-primary", brand.theme.primary);
    if (brand.theme.accent)
      r.style.setProperty("--brand-accent", brand.theme.accent);
  }, [brand.theme.primary, brand.theme.accent]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const reader = await getCurrentReader();
        if (!reader) throw new Error("No Reader session");

        if (!cancelled) {
          console.log("[FollowAuthor] reader session found");
          setReaderSession(reader);
        }
      } catch {
        if (!cancelled) {
          console.log("[FollowAuthor] no reader session");
          setReaderSession(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(getAuthorCartCount());
    };

    updateCartCount();

    window.addEventListener("bookish-cart-updated", updateCartCount);
    window.addEventListener("storage", updateCartCount);

    return () => {
      window.removeEventListener("bookish-cart-updated", updateCartCount);
      window.removeEventListener("storage", updateCartCount);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const readerSub = readerSession?.readerSub;
      const request = ++circleRequest.current;
      if (!readerSub || !shopOwnerSub || readerSub === shopOwnerSub) {
        if (!cancelled) {
          setIsFollowingAuthor(false);
          setAuthorFollowError("");
          setIsAuthorFollowLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setIsAuthorFollowLoading(true);
        setAuthorFollowError("");
      }

      try {
        const graph = await getReaderFollowGraph();
        const following = (group ? graph.groups || [] : graph.authors).some((author) => author.id === shopOwnerSub);
        if (!cancelled && request === circleRequest.current) setIsFollowingAuthor(following);
      } catch {
        if (!cancelled && request === circleRequest.current) setAuthorFollowError("");
      } finally {
        if (!cancelled && request === circleRequest.current) setIsAuthorFollowLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [readerSession?.readerSub, shopOwnerSub, group]);

  const toReaderAuth = () => {
    navigate(getReaderLoginPath(window.location.href));
  };

  const handleAuthorFollowToggle = async () => {
    if (!shopOwnerSub) return;
    setIsAuthorFollowLoading(true);
    setAuthorFollowError("");
    try {
      const currentReader = await getCurrentReader();
      setReaderSession(currentReader);
      const readerSub = currentReader?.readerSub;
      if (!readerSub) {
        toReaderAuth();
        return;
      }
      if (readerSub === shopOwnerSub) return;
      ++circleRequest.current;

      const graph = await getReaderFollowGraph();
      const following = (group ? graph.groups || [] : graph.authors).some(author => author.id === shopOwnerSub);
      if (following) {
        await (group ? unfollowGroup : unfollowAuthor)({ id: shopOwnerSub, name: brand.penName || shopOwnerSub });
        setIsFollowingAuthor(false);
      } else {
        await (group ? followGroup : followAuthor)({ id: shopOwnerSub, name: brand.penName || shopOwnerSub });
        setIsFollowingAuthor(true);
      }
    } catch {
      setAuthorFollowError("Could not update Circle membership. Please try again.");
    } finally {
      setIsAuthorFollowLoading(false);
    }
  };

  const showAuthorFollowControl = Boolean(shopOwnerSub) && readerSession?.readerSub !== shopOwnerSub;

  const authorFollowLabel = !readerSession?.readerSub
    ? (group ? "Join Circle" : `Join ${displayLabel(brand.penName || 'Author')}'s Circle`)
    : isFollowingAuthor
    ? "Joined ✓ / Leave Circle"
    : (group ? "Join Circle" : `Join ${displayLabel(brand.penName || 'Author')}'s Circle`);
  const layoutBgStyle = brand?.theme?.bgImage
    ? {
        backgroundImage: `url(${brand.theme.bgImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }
    : undefined;

  const baseNav = [
    { to: "", label: "Home", icon: Home },
    { to: "bookstore", label: "Books", icon: BookOpen },
    ...(group ? [{ to: "authors", label: "Authors", icon: Users }] : []),
    ...(hasBlog ? [{ to: "authorblog", label: "Blog", icon: PenSquare }] : []),
    { to: "newsletter", label: "Newsletter", icon: Newspaper },
    ...customPages.map((page) => ({ to: page.slug, label: displayLabel(page.title), icon: PageIcon })),
  ];

  if (authorHost && !group && !publicAuthor) {
    return <main className="p-8 text-bookish-purple" role="status">Loading this Circle…</main>;
  }
  if (authorHost && !group && publicAuthor?.unavailable) {
    return <main className="p-8 text-bookish-purple"><h1 className="text-2xl font-serif">This Circle is unavailable</h1><p className="my-4">This member’s shop is not currently available.</p><a className="underline" href="https://bookishcircle.com/market">Explore the Bookish Market</a></main>;
  }
  return (
    <div
      className="author-storefront min-h-screen flex flex-col md:flex-row bg-bookish-cream text-bookish-purple overflow-x-hidden"
      style={layoutBgStyle}
    >
      <header className="fixed top-0 left-0 right-0 z-40 bg-bookish-royal text-bookish-cream shadow">
        <div className="px-4 md:px-6 py-1 md:py-2 flex items-center justify-between">
          <p className="text-sm md:text-base font-semibold">
            {brand.penName
              ? `${displayLabel(brand.penName)}'s Circle`
              : group ? "Group Circle" : "Author Circle"}
          </p>

          <div className="flex items-center gap-3">
            {readerSession && <a href={canonicalUrl("/readerportal/mainpage?tab=myorders")} className="text-xs md:text-sm hover:underline">My Reader Portal</a>}
          {showAuthorFollowControl && (
              <button
                type="button"
                onClick={handleAuthorFollowToggle}
                disabled={isAuthorFollowLoading}
                className="rounded-full border border-white/70 px-3 py-1 text-xs md:text-sm font-semibold text-bookish-cream hover:bg-white/15 disabled:opacity-60"
              >
                {isAuthorFollowLoading ? "Updating..." : authorFollowLabel}
              </button>
            )}
            {!group && <button
              type="button"
              onClick={() => navigate("/cart")}
              className="relative inline-flex items-center justify-center rounded-full p-2 text-bookish-cream hover:bg-white/15 transition"
              aria-label={`Shopping cart with ${cartCount} item${cartCount === 1 ? "" : "s"}`}
              title="Shopping Cart"
            >
              <ShoppingCart className="h-5 w-5" />

              {cartCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold text-bookish-purple">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>}
            <a
              href={canonicalUrl("/market")}
              className="text-xs md:text-sm text-bookish-cream hover:text-bookish-cream"
            >
              The Bookish Circle Market
            </a>
          </div>
        </div>

        {authorFollowError && <p role="alert" className="md:hidden px-4 pb-2 text-sm text-bookish-cream">{authorFollowError}</p>}
      </header>
      <aside className="hidden md:flex w-64 flex-col relative overflow-hidden bg-gradient-to-b from-[#F3E7D2] via-[#F1E1C7] to-[#EAD6B6] border-r border-bookish-purple/15 p-6 pt-20 items-center">
        <div className="relative z-10 flex flex-col items-center space-y-4 w-full">
          <img
            src={brand.profileImageUrl || Logo}
            alt={group ? group.groupName : "Author"}
            className={
              brand.profileImageUrl
                ? "w-28 h-28 rounded-full object-cover border border-bookish-royal"
                : "w-28 h-auto"
            }
          />

          <h2 className="text-lg font-serif font-semibold text-bookish-purple tracking-[0.05em] text-center">
            {displayLabel(brand.penName || "Author")}
          </h2>

          {showAuthorFollowControl && (
            <button
              type="button"
              onClick={handleAuthorFollowToggle}
              disabled={isAuthorFollowLoading}
              className="w-full rounded-full bg-bookish-purple text-white px-4 py-2 text-sm font-semibold hover:bg-bookish-purple/90 disabled:opacity-60"
            >
              {isAuthorFollowLoading ? "Updating..." : authorFollowLabel}
            </button>
          )}

          {authorFollowError && (
            <p role="alert" className="text-xs text-bookish-purple text-center">{authorFollowError}</p>
          )}

          <nav className="flex flex-col w-full mt-4">
            {baseNav.map(({ to, label }) => (
              <NavLink
                key={to || "home"}
                to={to}
                end={to === ""}
                className={({ isActive }) =>
                  `w-full px-4 py-3 text-center uppercase tracking-wider font-semibold text-sm transition border-l-4 ${
                    isActive
                      ? "bg-bookish-purple/10 text-bookish-purple border-bookish-royal"
                      : "text-bookish-purpleAccent/80 border-transparent hover:bg-bookish-purple/10"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {showDashboardButton && (
            <a
              href="/authorportal/dashboard"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm text-bookish-purple hover:bg-bookish-purple/5 transition"
            >
              Return to Dashboard
            </a>
          )}
        </div>
      </aside>

      <div className="storefront-content flex flex-1 w-full pt-12 md:pt-0">
        <nav className="storefront-mobile-nav" aria-label="Author navigation">{baseNav.map(({ to, label }) => <NavLink key={to || "home"} to={to} end={to === ""} className={({ isActive }) => isActive ? "font-bold underline" : ""}>{label}</NavLink>)}</nav>

      <main className="flex-1 min-w-0 overflow-y-auto w-full pt-0 md:pt-10">
        {brand?.theme?.bannerImage && (
          <div className="w-full">
            <img
              src={brand.theme.bannerImage}
              alt=""
              className="w-full h-40 md:h-56 object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div className="storefront-page p-6">
          {!group && /\/bookstore\/?$/.test(location.pathname) && publicAuthor?.publishingGroup && <p className="mb-4 text-sm font-semibold text-bookish-purple"><a className="underline hover:no-underline" href={publicAuthor.publishingGroup.url}>Author member of {publicAuthor.publishingGroup.groupName}</a></p>}
          {children || <Outlet />}
        </div>
      </main>
      </div>
    </div>
  );
};

export default ReaderShopLayout;
